import React from "react";
import { AlertCircle, AlertTriangle, FileText, User } from "lucide-react";
import type { ComponentType } from "react";
import { C, FONT } from "../styles";
import type { AttentionItem as AttentionItemType, AttentionIcon } from "../types";

interface AttentionItemProps {
  item: AttentionItemType;
  isLast: boolean;
  onMarkDone: () => void;
}

interface IconColor {
  bg: string;
  fg: string;
  Icon: ComponentType<{ size?: number }>;
}

const ICON_COLORS: Record<AttentionIcon, IconColor> = {
  red: { bg: C.redSoft, fg: C.red, Icon: AlertCircle },
  amber: { bg: C.amberSoft, fg: C.amber, Icon: User },
  blue: { bg: C.blueSoft, fg: C.blue, Icon: AlertTriangle },
  green: { bg: C.greenSoft, fg: C.green, Icon: FileText },
};

export default function AttentionItem({ item, isLast, onMarkDone }: AttentionItemProps) {
  const ic = ICON_COLORS[item.icon] || ICON_COLORS.amber;
  const Icon = ic.Icon;

  const daysText =
    item.daysUntil !== null && item.daysUntil !== undefined
      ? item.daysUntil < 0
        ? " · " +
          Math.abs(item.daysUntil) +
          " day" +
          (Math.abs(item.daysUntil) === 1 ? "" : "s") +
          " ago"
        : item.daysUntil === 0
        ? " · today"
        : item.daysUntil === 1
        ? " · tomorrow"
        : item.urgency === "approaching"
        ? " · " + item.daysUntil + " days"
        : ""
      : "";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 22px",
        borderBottom: isLast ? "none" : "1px solid " + C.lineSoft,
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          display: "grid",
          placeItems: "center",
          flex: "0 0 38px",
          background: ic.bg,
          color: ic.fg,
        }}
      >
        <Icon size={19} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{item.title}</div>
        <div style={{ fontSize: 12.5, color: C.muted }}>
          {item.amount ? (
            <>
              <span
                style={{
                  color: "#0E1A1A",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                ${Number(item.amount).toFixed(2)}
              </span>
              <span style={{ color: "#1A2332" }}>
                {" "}
                {item.urgency === "overdue" ? "was due" : "due"}{" "}
              </span>
              <span
                style={{
                  color:
                    item.urgency === "urgent" || item.urgency === "overdue"
                      ? "#A32D2D"
                      : item.urgency === "approaching"
                      ? "#854F0B"
                      : "#1A2332",
                  fontWeight: item.urgency === "normal" ? 600 : 700,
                }}
              >
                {item.dueDate}
                {daysText}
              </span>
            </>
          ) : (
            item.desc
          )}
        </div>
      </div>
      <button
        onClick={item.onClick || onMarkDone}
        style={{
          background: "#fff",
          color: C.ink,
          border: "1px solid " + C.line,
          borderRadius: 9,
          padding: "8px 16px",
          fontWeight: 600,
          fontSize: 13,
          cursor: "pointer",
          fontFamily: FONT,
        }}
      >
        {item.action}
      </button>
    </div>
  );
}